import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { HealthResponse } from '@jabberr/core';
import { api } from '../lib/api.js';

export type AiState = 'available' | 'loading' | 'unavailable';

export interface HealthState {
  health: HealthResponse | null;
  aiState: AiState;
  lastError: string | null;
}

const HealthContext = createContext<HealthState>({ health: null, aiState: 'loading', lastError: null });

export const HEALTH_POLL_MS = 20_000;

export function aiStateFrom(h: HealthResponse | null, lastError: string | null = null): AiState {
  if (!h) return lastError ? 'unavailable' : 'loading';
  switch (h.inference.state) {
    case 'available':
      return 'available';
    case 'unavailable':
      return 'unavailable';
    case 'unknown':
      return 'loading';
  }
}

export function HealthProvider({ children, enabled }: { children: ReactNode; enabled: boolean }): ReactNode {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const ctrl = new AbortController();
    const tick = async (): Promise<void> => {
      try {
        const h = await api.health(ctrl.signal);
        if (!alive) return;
        setHealth(h);
        setLastError(null);
      } catch (err) {
        if (!alive) return;
        setLastError(err instanceof Error ? err.message : String(err));
        setHealth((prev) =>
          prev ? { ...prev, inference: { ...prev.inference, state: 'unavailable' } } : prev,
        );
      }
    };
    void tick();
    const timer = setInterval(() => void tick(), HEALTH_POLL_MS);
    return () => {
      alive = false;
      ctrl.abort();
      clearInterval(timer);
    };
  }, [enabled]);

  return <HealthContext.Provider value={{ health, aiState: aiStateFrom(health, lastError), lastError }}>{children}</HealthContext.Provider>;
}

export function useHealth(): HealthState {
  return useContext(HealthContext);
}
